import React, { useEffect, useState } from 'react';
import { http, authCfg } from '../../lib/api';
import { useToast } from '../../hooks/use-toast';
import { Gift, Loader2, RefreshCw, CheckCircle2, Clock, Send } from 'lucide-react';

const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('en-AU') : '—');

export default function GiftCardsPanel() {
  const { toast } = useToast();
  const [data, setData] = useState(null);
  const [busyId, setBusyId] = useState('');

  const load = () => http.get('/admin/gift-cards', authCfg()).then((r) => setData(r.data)).catch(() => setData({ stats: {}, records: [] }));
  useEffect(() => { load(); }, []);

  const resend = async (r) => {
    setBusyId(r.id);
    try {
      const { data: res } = await http.post(`/admin/gift-cards/${r.id}/resend`, {}, authCfg());
      toast({ title: res.status === 'sent' ? `Gift card re-emailed to ${res.to}` : `Email ${res.status}` });
      load();
    } catch (e) { toast({ title: e?.response?.data?.detail || 'Could not resend', variant: 'destructive' }); }
    finally { setBusyId(''); }
  };

  if (!data) return <div className="py-16 text-center"><Loader2 className="animate-spin inline text-alp-gold" size={20} /></div>;
  const st = data.stats || {};

  return (
    <div data-testid="giftcards-panel">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h2 className="font-serif text-2xl text-alp-cream flex items-center gap-2"><Gift size={20} className="text-alp-gold" /> Gift Cards</h2>
        <button onClick={load} className="inline-flex items-center gap-1.5 text-xs tracking-[0.12em] uppercase border border-alp-line px-3 py-1.5 rounded text-alp-cream hover:border-alp-gold hover:text-alp-gold"><RefreshCw size={13} /> Refresh</button>
      </div>
      <p className="text-alp-gray text-sm mb-6">Sold at <span className="text-alp-gold">/gift-cards</span>. The card email goes out automatically when you mark the order <span className="text-alp-gold">Payment Confirmed</span>. Recipients redeem on their Account page — the amount becomes store credit. No expiry.</p>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8" data-testid="giftcards-stats">
        {[['Issued', st.issued || 0], ['Total value', money(st.totalValue)], ['Redeemed', st.redeemed || 0], ['Redeemed value', money(st.redeemedValue)], ['Outstanding', money(st.outstanding)]].map(([l, v]) => (
          <div key={l} className="border border-alp-line rounded-lg bg-alp-card/40 p-4">
            <p className="text-[10px] tracking-[0.2em] uppercase text-alp-gray">{l}</p>
            <p className="text-alp-gold font-serif text-xl mt-1">{v}</p>
          </div>
        ))}
      </div>

      <div className="border border-alp-line rounded-lg bg-alp-card/40 p-5">
        <p className="text-[10px] tracking-[0.25em] uppercase text-alp-gold mb-3">All gift cards</p>
        {data.records.length === 0 ? <p className="text-alp-gray text-sm py-6 text-center">No gift cards sold yet — they go live the moment someone buys one at /gift-cards.</p> : (
          <div className="border border-alp-line rounded divide-y divide-alp-line/50 max-h-[32rem] overflow-y-auto" data-testid="giftcards-list">
            {data.records.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block text-alp-cream">
                    <span className="text-alp-gold">{r.code}</span> · {money(r.amount)}
                    {r.occasion && r.occasion !== 'classic' ? <span className="ml-2 text-[10px] uppercase tracking-[0.1em] text-alp-gold/80 border border-alp-gold/30 rounded-full px-2 py-0.5">{r.occasion === 'thankyou' ? 'Thank You' : r.occasion}</span> : null}
                    {r.recipientName ? ` · for ${r.recipientName}` : ''}
                  </span>
                  <span className="block text-alp-gray text-xs">
                    bought by {r.buyerName || r.buyerEmail} · order {r.orderNumber} · sent to {r.sentTo || '—'} · {fmtDate(r.createdAt)}
                    {r.emailStatus === 'scheduled' && r.deliverOn ? <span className="text-alp-gold"> · 📅 scheduled for {r.deliverOn}</span> : r.emailStatus !== 'sent' ? ` · email ${r.emailStatus}` : ''}
                  </span>
                </span>
                {r.status === 'redeemed' ? (
                  <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs" data-testid={`giftcard-redeemed-${r.code}`}>
                    <CheckCircle2 size={14} /> Redeemed by {r.redeemedBy} · {fmtDate(r.redeemedAt)}
                  </span>
                ) : (
                  <span className="text-amber-300 text-xs inline-flex items-center gap-1.5"><Clock size={13} /> Active</span>
                )}
                <button onClick={() => resend(r)} disabled={busyId === r.id} data-testid={`giftcard-resend-${r.code}`}
                  className="inline-flex items-center gap-1.5 border border-alp-line text-alp-gray px-3 py-1.5 rounded text-[11px] tracking-[0.1em] uppercase hover:border-alp-gold hover:text-alp-gold disabled:opacity-50">
                  {busyId === r.id ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Resend
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
